// Tarification intelligente — suggestions de prix optimisés par l'IA
import { useState, useMemo } from "react";
import { Zap, CheckCircle, AlertCircle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import {
  useSmartPricing,
  useApplyPrice,
  useApplyAllPrices,
} from "@/hooks/admin/useAI";
import type { PriceSuggestion } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(n: number): string {
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function confidenceClass(pct: number): string {
  if (pct >= 80) return "text-green-700 bg-green-50";
  if (pct >= 60) return "text-amber-700 bg-amber-50";
  return "text-red-700 bg-red-50";
}

function deltaClass(delta: number): string {
  if (delta > 0) return "text-green-700";
  if (delta < 0) return "text-red-600";
  return "text-stone-500";
}

// ---------------------------------------------------------------------------
// Confirmation modal
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly isPending: boolean;
  readonly suggestionCount: number;
  readonly gainFcfa: number;
}

function ConfirmApplyAllModal({
  open,
  onClose,
  onConfirm,
  isPending,
  suggestionCount,
  gainFcfa,
}: ConfirmModalProps) {
  return (
    <AdminModal title="Appliquer tous les prix suggérés" open={open} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-stone-600">
          Vous êtes sur le point d'appliquer{" "}
          <span className="font-semibold text-stone-900">{suggestionCount} suggestions</span>{" "}
          de prix. Gain potentiel estimé :{" "}
          <span className="font-semibold text-stone-900">{formatFcfa(gainFcfa)}</span>.
        </p>
        <p className="text-xs text-stone-400">
          Cette action modifiera les prix de vente sur la boutique.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={onConfirm}
            className="rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-60"
          >
            {isPending ? "Application…" : "Confirmer"}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function SmartPricing() {
  const [showConfirm, setShowConfirm] = useState(false);
  const { data, isLoading, isError } = useSmartPricing();
  const applyPrice = useApplyPrice();
  const applyAll = useApplyAllPrices();

  const suggestions = data?.suggestions ?? [];
  const pendingCount = suggestions.filter((s) => !s.applied).length;
  const appliedCount = suggestions.filter((s) => s.applied).length;
  const gainFcfa = data?.potential_revenue_gain_fcfa ?? 0;

  const columns: readonly ColumnDef<PriceSuggestion>[] = useMemo(
    () => [
      {
        id: "sku",
        header: "SKU",
        cell: (row) => <span className="font-mono text-xs text-stone-500">{row.sku}</span>,
      },
      {
        id: "name",
        header: "Produit",
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-stone-900">{row.name}</p>
            <p className="text-xs text-stone-400">{row.brand}</p>
          </div>
        ),
      },
      {
        id: "current_price_fcfa",
        header: "Prix actuel",
        cell: (row) => <span className="text-sm text-stone-700">{formatFcfa(row.current_price_fcfa)}</span>,
      },
      {
        id: "suggested_price_fcfa",
        header: "Prix suggéré",
        cell: (row) => <span className="text-sm font-semibold text-stone-900">{formatFcfa(row.suggested_price_fcfa)}</span>,
      },
      {
        id: "price_delta_percent",
        header: "Variation",
        cell: (row) => {
          const delta = row.price_delta_percent ?? 0;
          return (
            <span className={`text-sm font-medium ${deltaClass(delta)}`}>
              {delta > 0 ? "+" : ""}
              {delta.toFixed(1)}%
            </span>
          );
        },
      },
      {
        id: "reason",
        header: "Raison",
        cell: (row) => <span className="text-xs text-stone-500">{row.reason}</span>,
      },
      {
        id: "confidence_percent",
        header: "Confiance",
        cell: (row) => (
          <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${confidenceClass(row.confidence_percent)}`}>
            {row.confidence_percent}%
          </span>
        ),
      },
      {
        id: "applied",
        header: "Statut",
        cell: (row) =>
          row.applied ? (
            <span className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              <CheckCircle className="h-3 w-3" />
              Appliqué
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded bg-stone-100 px-2 py-0.5 text-xs text-stone-500">
              En attente
            </span>
          ),
      },
      {
        id: "actions",
        header: "",
        cell: (row) =>
          row.applied ? null : (
            <button
              type="button"
              disabled={applyPrice.isPending}
              onClick={() =>
                applyPrice.mutate({ productId: row.product_id, price: row.suggested_price_fcfa })
              }
              className="inline-flex items-center gap-1 rounded-md bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-60"
            >
              <Zap className="h-3.5 w-3.5" />
              Appliquer
            </button>
          ),
      },
    ],
    [applyPrice],
  );

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Tarification intelligente</h1>
            <p className="mt-1 text-sm text-stone-500">
              Suggestions de prix optimisés par l'IA basées sur la demande et le stock
            </p>
          </div>
          <button
            type="button"
            disabled={pendingCount === 0 || applyAll.isPending}
            onClick={() => setShowConfirm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-60"
          >
            <Zap className="h-4 w-4" />
            Appliquer tout
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KPICard
            label="Suggestions en attente"
            value={isLoading ? "…" : String(pendingCount)}
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Gain potentiel"
            value={isLoading ? "…" : formatFcfa(gainFcfa)}
            icon={<Zap className="h-4 w-4" />}
          />
          <KPICard
            label="Appliquées ce mois"
            value={isLoading ? "…" : String(appliedCount)}
            icon={<CheckCircle className="h-4 w-4" />}
          />
        </div>

        {/* Table */}
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les suggestions. Veuillez réessayer.
          </div>
        ) : (
          <AdminDataTable
            data={suggestions}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="Aucune suggestion disponible pour le moment."
          />
        )}
      </div>

      <ConfirmApplyAllModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          applyAll.mutate();
          setShowConfirm(false);
        }}
        isPending={applyAll.isPending}
        suggestionCount={pendingCount}
        gainFcfa={gainFcfa}
      />
    </AdminLayout>
  );
}
